import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { formatDateTime, apiErrorMsg } from '@/lib/formatters';
import {
  MessageCircle, Send, Settings, FileText, Plus, Copy, Check,
  AlertTriangle, CheckCircle, XCircle, Clock, ArrowUpRight, Trash2, Eye
} from 'lucide-react';

const MSG_STATUS_BADGE = {
  sent: 'bg-emerald-100 text-emerald-800',
  delivered: 'bg-blue-100 text-blue-800',
  read: 'bg-indigo-100 text-indigo-800',
  failed: 'bg-red-100 text-red-800',
  pending: 'bg-amber-100 text-amber-800',
  not_configured: 'bg-slate-200 text-slate-600',
  received: 'bg-teal-100 text-teal-800',
};

export default function WhatsAppPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('messages');

  return (
    <div className="space-y-6" data-testid="whatsapp-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">WhatsApp Business</h1>
        <p className="text-slate-500 text-sm mt-0.5">Send templates, track conversations, manage your WhatsApp integration</p>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="messages" data-testid="wa-messages-tab"><MessageCircle size={14} className="mr-1" /> Messages</TabsTrigger>
          <TabsTrigger value="templates" data-testid="wa-templates-tab"><FileText size={14} className="mr-1" /> Templates</TabsTrigger>
          <TabsTrigger value="send" data-testid="wa-send-tab"><Send size={14} className="mr-1" /> Quick Send</TabsTrigger>
          {user?.role === 'director' && (
            <TabsTrigger value="settings" data-testid="wa-settings-tab"><Settings size={14} className="mr-1" /> Settings</TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="messages"><MessagesTab /></TabsContent>
        <TabsContent value="templates"><TemplatesTab user={user} /></TabsContent>
        <TabsContent value="send"><QuickSendTab /></TabsContent>
        {user?.role === 'director' && <TabsContent value="settings"><WASettingsTab /></TabsContent>}
      </Tabs>
    </div>
  );
}

function MessagesTab() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/whatsapp/messages?limit=100').then(r => { setMessages(r.data.messages); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const sent = messages.filter(m => m.direction === 'outbound').length;
  const received = messages.filter(m => m.direction === 'inbound').length;
  const failed = messages.filter(m => m.status === 'failed' || m.status === 'not_configured').length;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="border-slate-200 kpi-card"><CardContent className="p-4">
          <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Total Messages</p>
          <p className="text-2xl font-extrabold font-mono">{messages.length}</p>
        </CardContent></Card>
        <Card className="border-slate-200 kpi-card"><CardContent className="p-4">
          <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Sent</p>
          <p className="text-2xl font-extrabold font-mono text-emerald-700">{sent}</p>
        </CardContent></Card>
        <Card className="border-slate-200 kpi-card"><CardContent className="p-4">
          <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Received</p>
          <p className="text-2xl font-extrabold font-mono text-blue-700">{received}</p>
        </CardContent></Card>
        <Card className="border-slate-200 kpi-card"><CardContent className="p-4">
          <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Failed / Not Sent</p>
          <p className="text-2xl font-extrabold font-mono text-red-600">{failed}</p>
        </CardContent></Card>
      </div>

      <div className="table-container bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Direction</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Phone</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Lead</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Message</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Status</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Time</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={i}>{Array.from({ length: 6 }).map((_, j) => <TableCell key={j}><div className="h-4 bg-slate-100 rounded animate-pulse" /></TableCell>)}</TableRow>
            )) : messages.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-400">
                <MessageCircle size={32} className="mx-auto mb-2 text-slate-300" />
                No messages yet. Use Quick Send or connect WhatsApp API in Settings.
              </TableCell></TableRow>
            ) : messages.map(m => (
              <TableRow key={m._id} data-testid={`wa-msg-${m._id}`}>
                <TableCell>
                  <Badge variant="outline" className={`text-[10px] ${m.direction === 'inbound' ? 'border-teal-300 text-teal-700' : 'border-blue-300 text-blue-700'}`}>
                    {m.direction === 'inbound' ? '← In' : '→ Out'}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm font-mono text-slate-600">{m.phone}</TableCell>
                <TableCell className="text-xs text-slate-500">{m.lead_id || '-'}</TableCell>
                <TableCell className="text-sm max-w-xs truncate">{m.body}</TableCell>
                <TableCell>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${MSG_STATUS_BADGE[m.status] || 'bg-slate-100'}`}>
                    {m.status === 'not_configured' ? 'Not Sent' : m.status}
                  </span>
                </TableCell>
                <TableCell className="text-xs text-slate-400">{formatDateTime(m.created_at)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function TemplatesTab({ user }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const fetchTemplates = () => {
    api.get('/whatsapp/templates').then(r => { setTemplates(r.data.templates); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(() => { fetchTemplates(); }, []);

  const deleteTemplate = async (id) => {
    try {
      await api.delete(`/whatsapp/templates/${id}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (err) { toast.error(apiErrorMsg(err)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{templates.length} templates</p>
        {['director', 'manager'].includes(user?.role) && (
          <Button onClick={() => setShowCreate(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-template-btn">
            <Plus size={16} /> New Template
          </Button>
        )}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[1,2].map(i => <div key={i} className="h-32 bg-white rounded-xl border animate-pulse" />)}
        </div>
      ) : templates.length === 0 ? (
        <Card className="border-slate-200"><CardContent className="py-12 text-center text-slate-400">
          <FileText size={32} className="mx-auto mb-2 text-slate-300" />
          No templates yet. Create templates for common messages like appointment confirmations, follow-up reminders, and booking updates.
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {templates.map(t => (
            <Card key={t._id} className="border-slate-200" data-testid={`wa-template-${t._id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-semibold">{t.name}</CardTitle>
                  <div className="flex items-center gap-1">
                    <Badge variant="outline" className="text-[10px]">{t.category}</Badge>
                    <Badge variant="outline" className="text-[10px]">{t.status}</Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 whitespace-pre-wrap bg-slate-50 p-3 rounded-lg border border-slate-100">{t.body}</p>
                {t.variables?.length > 0 && (
                  <p className="text-xs text-slate-400 mt-2">Variables: {t.variables.join(', ')}</p>
                )}
                <div className="flex items-center justify-between mt-3">
                  <span className="text-xs text-slate-400">{t.language || 'en'}</span>
                  {['director', 'manager'].includes(user?.role) && (
                    <Button variant="ghost" size="sm" className="h-7 text-red-500 hover:text-red-700" onClick={() => deleteTemplate(t._id)}>
                      <Trash2 size={14} />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {showCreate && <CreateTemplateDialog open onClose={() => setShowCreate(false)} onCreated={() => { fetchTemplates(); setShowCreate(false); }} />}
    </div>
  );
}

function CreateTemplateDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', category: 'utility', language: 'en', body: '', variables: '' });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/whatsapp/templates', {
        ...form,
        variables: form.variables ? form.variables.split(',').map(s => s.trim()) : [],
      });
      toast.success('Template created');
      onCreated();
    } catch (err) { toast.error(apiErrorMsg(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create WhatsApp Template</DialogTitle>
          <p className="text-sm text-slate-500">Define a reusable message template for WhatsApp Business</p>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div><Label>Template Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. site_visit_reminder" required data-testid="template-name-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="utility">Utility</SelectItem>
                  <SelectItem value="marketing">Marketing</SelectItem>
                  <SelectItem value="authentication">Authentication</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Language</Label>
              <Select value={form.language} onValueChange={v => setForm({ ...form, language: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="hi">Hindi</SelectItem>
                  <SelectItem value="gu">Gujarati</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Message Body *</Label>
            <Textarea value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} rows={4}
              placeholder={"Hello {{1}}, this is a reminder for your site visit at {{2}} on {{3}}. Looking forward to meeting you!"} required data-testid="template-body-input" />
            <p className="text-xs text-slate-400 mt-1">Use {'{{1}}'}, {'{{2}}'} etc. for dynamic variables</p>
          </div>
          <div><Label>Variables (comma-separated)</Label><Input value={form.variables} onChange={e => setForm({ ...form, variables: e.target.value })} placeholder="customer_name, project_name, date" /></div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="submit-template-btn">{saving ? 'Creating...' : 'Create Template'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function QuickSendTab() {
  const [form, setForm] = useState({ phone: '', lead_id: '', message: '', template_id: '' });
  const [templates, setTemplates] = useState([]);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get('/whatsapp/templates').then(r => setTemplates(r.data.templates)).catch(() => {});
  }, []);

  const handleSend = async () => {
    if (!form.phone) return toast.error('Phone number is required');
    if (!form.message && !form.template_id) return toast.error('Enter a message or select a template');
    setSending(true);
    setResult(null);
    try {
      const { data } = await api.post('/whatsapp/send', form);
      setResult(data);
      if (data.sent) {
        toast.success('Message sent via WhatsApp');
        setForm({ ...form, message: '', template_id: '' });
      } else {
        toast.warning(data.message || `Status: ${data.status}`);
      }
    } catch (err) { toast.error(apiErrorMsg(err)); }
    finally { setSending(false); }
  };

  return (
    <div className="max-w-lg space-y-4">
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold">Send WhatsApp Message</CardTitle>
          <CardDescription>Send a message or template to any phone number</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Phone Number *</Label>
              <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="919825011001" data-testid="wa-send-phone" />
              <p className="text-xs text-slate-400 mt-0.5">Country code + number, no spaces</p>
            </div>
            <div>
              <Label>Lead ID (optional)</Label>
              <Input value={form.lead_id} onChange={e => setForm({ ...form, lead_id: e.target.value })} placeholder="VIR-0001" data-testid="wa-send-lead-id" />
            </div>
          </div>
          <div>
            <Label>Or pick a Template</Label>
            <Select value={form.template_id} onValueChange={v => setForm({ ...form, template_id: v === 'none' ? '' : v, message: '' })}>
              <SelectTrigger data-testid="wa-template-select"><SelectValue placeholder="No template (free text)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No template (free text)</SelectItem>
                {templates.map(t => <SelectItem key={t._id} value={t._id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {!form.template_id && (
            <div>
              <Label>Message *</Label>
              <Textarea value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} rows={3} placeholder="Hi! I'm calling from SalesOS regarding your property enquiry..." data-testid="wa-send-message" />
            </div>
          )}
          <Button onClick={handleSend} disabled={sending} className="w-full bg-[#25D366] hover:bg-[#1DA851] text-white font-semibold" data-testid="wa-send-btn">
            <Send size={16} /> {sending ? 'Sending...' : 'Send via WhatsApp'}
          </Button>

          {result && (
            <div className={`p-3 rounded-lg text-sm ${result.sent ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-amber-50 text-amber-800 border border-amber-200'}`} data-testid="wa-send-result">
              {result.sent ? (
                <div className="flex items-center gap-2"><CheckCircle size={16} /> Message sent successfully</div>
              ) : (
                <div>
                  <div className="flex items-center gap-2 font-medium"><AlertTriangle size={16} /> {result.status === 'not_configured' ? 'WhatsApp API Not Configured' : 'Message Not Sent'}</div>
                  <p className="mt-1 text-xs">{result.message || result.error}</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function WASettingsTab() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ phone_number_id: '', business_account_id: '', access_token: '', verify_token: '' });
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState('');

  useEffect(() => {
    api.get('/whatsapp/config').then(r => {
      setConfig(r.data);
      setForm({
        phone_number_id: r.data.phone_number_id || '',
        business_account_id: r.data.business_account_id || '',
        access_token: '',
        verify_token: r.data.verify_token || '',
      });
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/whatsapp/config', form);
      toast.success('WhatsApp configuration saved');
      const { data } = await api.get('/whatsapp/config');
      setConfig(data);
    } catch (err) { toast.error(apiErrorMsg(err)); }
    finally { setSaving(false); }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  if (loading) return <div className="text-center py-8 text-slate-400">Loading...</div>;

  return (
    <div className="max-w-2xl space-y-6">
      {/* Status Banner */}
      <Card className={`border ${config?.configured ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}>
        <CardContent className="p-4 flex items-center gap-3">
          {config?.configured ? (
            <><CheckCircle size={20} className="text-emerald-600" /><div><p className="font-semibold text-emerald-800">WhatsApp Business API Connected</p><p className="text-xs text-emerald-600">Messages will be delivered via the official API</p></div></>
          ) : (
            <><AlertTriangle size={20} className="text-amber-600" /><div><p className="font-semibold text-amber-800">WhatsApp Business API Not Configured</p><p className="text-xs text-amber-600">Messages will be logged but not delivered. Add your credentials below.</p></div></>
          )}
        </CardContent>
      </Card>

      {/* Webhook URL */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Webhook URL</CardTitle></CardHeader>
        <CardContent>
          <p className="text-xs text-slate-500 mb-2">Configure this URL in your Meta WhatsApp Business dashboard for incoming messages and delivery updates:</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-slate-50 p-2.5 rounded-lg text-sm font-mono border border-slate-200 truncate">{config?.webhook_url}</code>
            <Button variant="outline" size="sm" onClick={() => copyToClipboard(config?.webhook_url, 'webhook')} data-testid="copy-webhook-url">
              {copied === 'webhook' ? <Check size={14} /> : <Copy size={14} />}
            </Button>
          </div>
          {config?.verify_token && (
            <div className="mt-2">
              <p className="text-xs text-slate-500 mb-1">Verify Token:</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-slate-50 p-2 rounded-lg text-xs font-mono border border-slate-200">{config.verify_token}</code>
                <Button variant="outline" size="sm" onClick={() => copyToClipboard(config.verify_token, 'token')}>
                  {copied === 'token' ? <Check size={14} /> : <Copy size={14} />}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* API Credentials */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">API Credentials</CardTitle>
          <CardDescription>From Meta Business Suite → WhatsApp → API Setup</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div><Label>Phone Number ID</Label><Input value={form.phone_number_id} onChange={e => setForm({ ...form, phone_number_id: e.target.value })} placeholder="e.g. 1234567890" data-testid="wa-phone-id-input" /></div>
          <div><Label>Business Account ID</Label><Input value={form.business_account_id} onChange={e => setForm({ ...form, business_account_id: e.target.value })} placeholder="e.g. 9876543210" /></div>
          <div>
            <Label>Access Token</Label>
            <Input type="password" value={form.access_token} onChange={e => setForm({ ...form, access_token: e.target.value })} placeholder={config?.access_token_set ? '••••••••• (already set)' : 'Paste your permanent access token'} data-testid="wa-access-token-input" />
            <p className="text-xs text-slate-400 mt-0.5">Your token is encrypted and never displayed after saving</p>
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-wa-config-btn">
            {saving ? 'Saving...' : 'Save Configuration'}
          </Button>
        </CardContent>
      </Card>

      {/* Setup Guide */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Setup Guide</CardTitle></CardHeader>
        <CardContent className="text-sm text-slate-600 space-y-2">
          <p><strong>1.</strong> Go to <a href="https://business.facebook.com" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline">Meta Business Suite</a> and create a WhatsApp Business account.</p>
          <p><strong>2.</strong> Navigate to WhatsApp → API Setup and note your Phone Number ID, Business Account ID, and generate a permanent Access Token.</p>
          <p><strong>3.</strong> Under Webhooks, subscribe to <code className="bg-slate-100 px-1 rounded">messages</code> and paste the Webhook URL and Verify Token shown above.</p>
          <p><strong>4.</strong> Submit message templates for approval under WhatsApp Manager → Message Templates.</p>
          <p className="text-xs text-slate-400 pt-2 border-t border-slate-100">SalesOS uses the official WhatsApp Cloud API. No unofficial automation or scraping is used.</p>
        </CardContent>
      </Card>
    </div>
  );
}
